import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { emailArrayValidator, isValidEmailAddress } from '../../validators/email';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { commitUserValue } from './user-edit.util';

/**
 * Multiple e-mail addresses as chips (#21163). The single-address `email` field stays as it is:
 * this is a separate type so the two keep separate value shapes — `email` writes a string,
 * `email-tags` writes `string[]` — instead of one field type whose datatype flips on a toggle.
 *
 * The chips are the shared Figma Filter chip (`.dd-tag-chip`, styled in
 * `src/styles/components/_chips.scss`), with a permanently visible remove cross — free text only,
 * so none of the tags field's option, menu or autocomplete machinery applies here.
 */
@Component({
  selector: 'formly-field-custom-email-tags',
  template: `
    <!-- The tags-list class is load-bearing, not decoration: _forms.scss caps every
         .mdc-text-field inside a form at 40px and lifts that cap for .tags-list. Without it the
         box stays one row high while the chips wrap underneath it, pushing the input line
         outside the field (#21163). -->
    <mat-form-field
      class="tags-list w-full"
      floatLabel="always"
      [class.mat-form-field-invalid]="inputError() || (formControl.invalid && formControl.touched)">
      <mat-label>
        <!-- The literal '*' is the only required marker this field can render: Material gates its
             own on the form-field control reporting required, and that control is the chip grid,
             which carries neither [required] nor a form control. Same reason as tags.component. -->
        <app-field-label-row
          [label]="(props.label ?? '') + (props.required ? ' *' : '')"
          [required]="props.required"
          [info]="props.info"></app-field-label-row>
      </mat-label>
      <!-- [disabled] on the grid, not only on the chips: the grid is this mat-form-field's
           control, and the label/border greys come from what IT reports. Without it a disabled
           field keeps a black label above greyed-out chips. -->
      <mat-chip-grid #chipGrid class="dd-email-chips" [disabled]="isDisabled()" [attr.aria-label]="props.label">
        @for (email of emails(); track email) {
          <!-- [disabled] tracks the DISABLED state only. A read-only field keeps its chips at
               full contrast (_forms.scss: "read-only fields keep full-contrast text") and only
               loses the controls; routing readonly through disabled dimmed the addresses twice,
               down to 1.4:1. -->
          <mat-chip-row class="dd-tag-chip" [disabled]="isDisabled()" [removable]="isInteractive()" (removed)="remove(email)">
            <span class="dd-tag-chip__label" [title]="email">{{ email }}</span>
            @if (isInteractive()) {
              <!-- matChipRemove on the button only: on the icon as well it nests an interactive
                   element inside the button and announces two remove controls per chip. -->
              <button matChipRemove class="dd-tag-remove" [attr.aria-label]="email + ' verwijderen'">
                <mat-icon aria-hidden="true" [svgIcon]="'close'"></mat-icon>
              </button>
            }
          </mat-chip-row>
        }
        <!-- No input at all while disabled — the same shape the tags field's read-only view has.
             A disabled-but-present text box is dead weight, and its text colour would not follow
             the chips' disabled grey (Material greys neither), so the field would render two
             different "disabled" colours side by side. -->
        @if (isInteractive()) {
          <input
            matInput
            #input
            type="email"
            class="pl-2"
            [placeholder]="emails().length ? '' : placeholder()"
            [formlyDataCy]="field"
            [matChipInputFor]="chipGrid"
            [matChipInputSeparatorKeyCodes]="separatorKeyCodes"
            [matChipInputAddOnBlur]="true"
            (matChipInputTokenEnd)="add($event)" />
        }
      </mat-chip-grid>
      <!-- Only a configured description renders. There is deliberately no built-in "confirm with
           Enter or comma" hint: a chips field does not explain itself elsewhere either, and a
           permanent instruction under every field is noise. -->
      @if (props.description) {
        <mat-hint class="inline-block mb-2">{{ props.description }}</mat-hint>
      }
    </mat-form-field>
    @if (inputError()) {
      <!-- The rejected text is still IN the input, so it has no control to report an error on;
           app-formly-error only renders errors of the field's own control. -->
      <mat-error class="px-4 text-sm mb-4" data-cy="formly-error" role="alert" aria-live="assertive"
        >Voer een geldig e-mailadres in</mat-error
      >
    } @else {
      <app-formly-error [field]="field"></app-formly-error>
    }
  `,
  styles: `
    /* The remove cross is ALWAYS visible (same as linkage-tags). The tags field hides it until
       hover because its chips are also removable by clicking the chip itself; here the cross is
       the only way out, and a control you have to discover by hovering is not one (#21163).
       No opacity ramp either: _chips.scss already paints it in the label's own accent token, and
       dimming it to 0.7 made it read as a second, lighter colour next to the address. */

    /* Always visible, here and not in _chips.scss: at component specificity this only has to
       beat Material's 0.54 default, while the same declaration in the shared file would also
       outrank the tags field's own hide-until-hover rule. */
    .dd-tag-remove {
      opacity: 1;
    }

    /* Chip padding for a trailing-remove chip lives in _chips.scss, next to the geometry it
       corrects: linkage-tags has the identical anatomy, and a per-component !important override
       fighting Material's own rule was the wrong place for it. */
    .dd-tag-remove .mat-icon {
      width: 14px;
      height: 14px;
      font-size: 14px;
    }
  `,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    MatError,
    MatIcon,
    FormlyDataCyDirective,
    FormlyErrorComponent,
    FieldLabelRowComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyEmailTagsComponent extends FieldType implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  /** Enter and comma. A pasted list is split in `add()`, so semicolons and spaces work too. */
  readonly separatorKeyCodes = [13, 188];
  readonly emails = signal<string[]>([]);
  /** True while the input holds text that is not a valid address. */
  readonly inputError = signal(false);
  /**
   * What "no addresses" looked like when the entry loaded — `[]` if the entry already carried an
   * array, `null` otherwise. Removing the last chip restores exactly that, so adding an address
   * and taking it off again leaves the value equal to the saved one and the save button goes
   * back to disabled (#21026 promises exactly that round trip; DetailsService compares values).
   */
  #emptyValue: string[] | null = null;

  ngOnInit(): void {
    // addValidators, not setValidators: a form-builder configured validator on the same field
    // must survive (the single-address email field overwrites, and loses them).
    this.formControl.addValidators(emailArrayValidator);
    this.formControl.updateValueAndValidity({ emitEvent: false });

    this.#syncFromControl();
    // A value this field cannot render is normalised ONCE, on init: a module field switched from
    // `email` to `email-tags` still holds a plain string, and a mapping or interface action can
    // write one too. Rendering zero chips over a value the entry still saves back is worse than
    // reshaping it — the user sees an empty field, the array validator sees nothing to flag, and
    // the stale string is PUT back untouched.
    //
    // Silent on purpose: this is normalisation, not a user edit, so it must NOT mark the control
    // dirty (see user-edit.util.ts) — DetailsService folds it into its saved-value baseline.
    // A STRING only. Any other shape (a number, an object left by a different field type) is
    // left untouched on purpose: reshaping it would silently replace a value this field cannot
    // render, and the user would save the loss without ever seeing it. emailArrayValidator flags
    // those instead, so the entry refuses to save until someone looks at it.
    const raw = this.formControl.value;
    this.#emptyValue = Array.isArray(raw) ? [] : null;
    if (typeof raw === 'string' && raw.trim()) {
      this.formControl.setValue(this.emails(), { emitEvent: false });
    }

    // Hydration and external writes (mapping, interface action, SignalR refresh) land on the
    // control, never on the signal that renders the chips.
    this.formControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#syncFromControl());
  }

  /**
   * Method, not a signal: `props.disabled` is a plain object field that formly expressions rewrite
   * at runtime, so a signal set once in ngOnInit would go stale. Same shape as
   * ClearableFieldType.isDisabled(), and consulting the field's OWN control (never an ancestor
   * group) keeps `expressions: { 'props.disabled': … }` authoritative.
   */
  isDisabled(): boolean {
    return this.formControl.disabled || !!this.props.disabled;
  }

  /**
   * Adding and removing addresses is off while the field is disabled.
   *
   * Disabled only: read mode never reaches this component. `email-tags` is a wrappable type, so
   * `props.readonly` makes `ReadonlyWrapper` render a label/value row INSTEAD of the field —
   * Formly never builds the type component at all (readonly/readonly.wrapper.ts, #21239).
   */
  isInteractive(): boolean {
    return !this.isDisabled();
  }

  /**
   * A configured placeholder wins; otherwise an example address. It shows the FORMAT without
   * repeating the label — "Adres invoeren" read as a street address to the first user who saw it.
   */
  placeholder(): string {
    return this.props.placeholder || 'naam@bedrijf.nl';
  }

  add(event: MatChipInputEvent): void {
    this.formControl.markAsTouched();
    const tokens = (event.value || '')
      .split(/[,;\s]+/)
      .map(token => token.trim())
      .filter(Boolean);

    if (tokens.length === 0) {
      event.chipInput.clear();
      this.inputError.set(false);
      return;
    }

    const accepted = tokens.filter(token => isValidEmailAddress(token));
    const rejected = tokens.filter(token => !isValidEmailAddress(token));

    const next = dedupeEmails([...this.emails(), ...accepted]);
    if (next.length !== this.emails().length) {
      this.#commit(next);
    }

    // Rejected text stays in the input so the user can fix a typo instead of retyping it.
    event.chipInput.inputElement.value = rejected.join(', ');
    this.inputError.set(rejected.length > 0);
  }

  remove(email: string): void {
    this.#commit(this.emails().filter(current => current !== email));
  }

  #commit(emails: string[]): void {
    this.emails.set(emails);
    const value = emails.length ? emails : this.#emptyValue;
    // Chips are written straight to the control, bypassing Angular's CVA pipeline, so the edit
    // has to be marked explicitly or the save button stays disabled (#21026).
    commitUserValue(this.formControl, value);
  }

  #syncFromControl(): void {
    this.emails.set(toEmailList(this.formControl.value));
  }
}

/** Case-insensitive dedup, first spelling wins — the mail server treats the casings as one. */
function dedupeEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  return emails.filter(email => {
    const key = email.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * Whatever the control holds → the chip list. A string (a field switched over from the
 * single-address `email` type, or a mapped value) becomes its addresses, and duplicates are
 * collapsed here as well as in `add()`: `@for` needs unique keys, and `remove()` matches by value,
 * so a duplicate that only hydration produced would drop both copies at once.
 */
function toEmailList(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(/[,;\s]+/) : [];
  return dedupeEmails(
    raw.filter((email): email is string => typeof email === 'string').map(email => email.trim()).filter(Boolean)
  );
}
